import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { persistState } from '@ngneat/elf-persist-state';
import { createRequestsStatusOperator, updateRequestStatus, withRequestsStatus } from '@ngneat/elf-requests';
import { AllergenEnum, UserLikesDishFamilyDto } from '@timechef/core/api';
import { capacitorStorageStrategy, getIdleRequestStatus, includeKeys, selectAppRequestStatus } from '@timechef/core/utils/elf-utils';
import { isSameDay, subDays } from 'date-fns';
import { map } from 'rxjs';
import { NB_SLIDES_TO_DISPLAY } from './account.data';
import { AntiGaspiAlertSource, AntiGaspiHistory, LikesFilterEnum, OnboardingInfos } from './account.models';

interface AccountProps {
  antiGaspiHistory: AntiGaspiHistory[];
  language: string | null;
  theme: 'light' | 'dark';
  allergens: {
    userAllergens: AllergenEnum[];
  };
  likes: {
    filter: LikesFilterEnum;
    currentCategory: string | null;
    categories: string[];
    dishFamilies: UserLikesDishFamilyDto[];
  };
  onboardingInfos: OnboardingInfos[];
  onboardingViewedKeys: string[];
  onboardingViewedDate: Date | null;
}

export enum AccountStatusKeys {
  Likes = 'likes',
  UpdateDishLikeNotification = 'update-dish-like-notification',
  Onboarding = 'onboarding',
}

@Injectable({ providedIn: 'root' })
export class AccountRepository {
  private store = createStore(
    { name: 'account' },
    withProps<AccountProps>({
      antiGaspiHistory: [],
      language: null,
      theme: 'light',
      allergens: {
        userAllergens: [],
      },
      likes: {
        filter: LikesFilterEnum.All,
        currentCategory: null,
        categories: [],
        dishFamilies: [],
      },
      onboardingInfos: [],
      onboardingViewedKeys: [],
      onboardingViewedDate: null,
    }),
    withRequestsStatus(),
  );

  instance = persistState(this.store, {
    key: 'account',
    storage: capacitorStorageStrategy,
    source: () => this.store.pipe(includeKeys(['language', 'theme', 'allergens', 'likes', 'onboardingViewedKeys', 'onboardingViewedDate', 'antiGaspiHistory'])),
  });

  likesMetadata$ = this.store.pipe(
    select((s) => ({
      categories: s.likes.categories.map((x) => ({ label: x, ref: x })),
      currentCategory: s.likes.currentCategory ? { label: s.likes.currentCategory!, ref: s.likes.currentCategory! } : null,
    })),
  );

  currentLikesFiltered$ = this.store.pipe(
    select((s) => {
      if (!s.likes.dishFamilies) return null;

      let { dishFamilies } = s.likes;

      if (s.likes.filter === LikesFilterEnum.OnlyNotifications)
        dishFamilies = dishFamilies.map((x) => ({ ...x, dishes: x.dishes.filter((d) => d.isNotificationEnabled) }));

      return dishFamilies;
    }),
  );

  language$ = this.store.pipe(select((s) => s.language));
  theme$ = this.store.pipe(select((s) => s.theme));
  likesRequestStatus$ = this.store.pipe(selectAppRequestStatus(AccountStatusKeys.Likes));
  updateDishLikeNotificationRequestStatus$ = (dishId: number) => this.store.pipe(selectAppRequestStatus(buildUpdateDishLikeNotificationKey(dishId)));
  updateDishLikeNotificationErrorTracker$ = this.store.pipe(
    selectAppRequestStatus(AccountStatusKeys.UpdateDishLikeNotification),
    map((x) => (x.value === 'error' ? x.error : null)),
  );

  updateOnboarding(onboardingInfos: OnboardingInfos[]) {
    this.store.update((s) => {
      return {
        ...s,
        onboardingInfos: onboardingInfos.sort(function (a, b) {
          return a.order - b.order;
        }),
      };
    });
  }

  getLanguage = () => this.store.query((s) => s.language);
  getTheme = () => this.store.query((s) => s.theme);
  getOnboardingInfos = () =>
    this.store.query((s) => s.onboardingInfos.filter((info) => !s.onboardingViewedKeys.includes(info.key)).slice(0, NB_SLIDES_TO_DISPLAY));
  getHasOnboardingInfosToDisplay = () =>
    this.store.query((s) => {
      if (s.onboardingViewedDate) return false;

      return s.onboardingInfos.filter((info) => !s.onboardingViewedKeys.includes(info.key)).length > 0;
    });

  setTheme(theme: 'light' | 'dark') {
    this.store.update((s) => ({ ...s, theme }));
  }

  changeLanguage(language: string) {
    this.store.update((s) => ({ ...s, language }));
  }

  getAllergens = () => this.store.query((s) => s.allergens.userAllergens);

  getIsElligibleToAntiGaspiAlert(restaurantId: number, source: AntiGaspiAlertSource) {
    const history = this.store.query((s) => s.antiGaspiHistory);

    // no history -> true
    if (!history || history.length === 0) return true;

    // If already prompted today for this restaurant, regardless of source -> false
    if (history.some((x) => x.restaurantId === restaurantId && isSameDay(new Date(), x.date))) {
      return false;
    }

    // If any previous alert for this restaurant and source -> false
    if (history.some((x) => x.restaurantId === restaurantId && x.source === source)) {
      return false;
    }

    return true;
  }

  addAntiGaspiAlertHistory(history: AntiGaspiHistory) {
    this.store.update((s) => {
      return {
        ...s,
        antiGaspiHistory: [...s.antiGaspiHistory, history],
      };
    });
  }

  DEBUG_clearAntiGaspiHistory() {
    this.store.update((s) => ({ ...s, antiGaspiHistory: [] }));
  }

  DEBUG_ShiftDateAntiGaspiHistory() {
    this.store.update((s) => ({ ...s, antiGaspiHistory: s.antiGaspiHistory.map((x) => ({ ...x, date: subDays(x.date, 1) })) }));
  }

  updateAllergen(allergenEnum: AllergenEnum, isChecked: boolean) {
    if (isChecked) {
      this.store.update((s) => ({ ...s, allergens: { ...s.allergens, userAllergens: [...s.allergens.userAllergens, allergenEnum] } }));
    } else {
      this.store.update((s) => ({
        ...s,
        allergens: { ...s.allergens, userAllergens: s.allergens.userAllergens.filter((e) => e !== allergenEnum) },
      }));
    }
  }

  getLikesFilter = () => this.store.query((s) => s.likes.filter);

  setOnboardingScreensAsSeen(newKeys: string[]) {
    this.store.update((s) => {
      // we update the viewed keys list
      const onboardingUpdatedKeys = s.onboardingViewedKeys;
      newKeys.forEach((newKey) => {
        //if the keys list doesn't include the new keys, we add it
        if (!onboardingUpdatedKeys.includes(newKey)) onboardingUpdatedKeys.push(newKey);
      });

      return {
        ...s,
        onboardingViewedKeys: onboardingUpdatedKeys,
        onboardingViewedDate: new Date(),
      };
    });
  }

  resetOnboardingCache = () => this.store.update((s) => ({ ...s, onboardingViewedKeys: [], onboardingViewedDate: null }));

  setLikeDishFamilies(dishFamilies: UserLikesDishFamilyDto[]) {
    const categories = dishFamilies.map((x) => x.label);

    this.store.update(
      (s) => ({ ...s, likes: { ...s.likes, dishFamilies, categories, currentCategory: categories[0] } }),
      updateRequestStatus(AccountStatusKeys.Likes, 'success'),
    );
  }

  setLikesCurrentCategoryByIndex(idx: number) {
    this.store.update((s) => ({ ...s, likes: { ...s.likes, currentCategory: s.likes.categories[idx] } }));
  }

  setLikesCurrentCategory(category: string) {
    this.store.update((s) => ({ ...s, likes: { ...s.likes, currentCategory: category } }));
  }

  setLikesFilter(filter: LikesFilterEnum) {
    this.store.update((s) => ({ ...s, likes: { ...s.likes, filter } }));
  }

  setDishLikeNotification(dishId: number, isNotificationEnabled: boolean) {
    this.store.update(
      (s) => ({
        ...s,
        likes: {
          ...s.likes,
          dishFamilies: [
            ...s.likes.dishFamilies.map((df) => ({
              ...df,
              dishes: [
                ...df.dishes.map((d) => {
                  if (d.id !== dishId) return d;

                  return {
                    ...d,
                    isNotificationEnabled,
                  };
                }),
              ],
            })),
          ],
        },
      }),
      updateRequestStatus(AccountStatusKeys.UpdateDishLikeNotification, 'success'),
      updateRequestStatus(buildUpdateDishLikeNotificationKey(dishId), 'success'),
    );
  }

  getUpdateDishLikeNotificationRequestStatus = (dishId: number) => {
    const key = buildUpdateDishLikeNotificationKey(dishId);
    return this.store.query((s) => (s.requestsStatus.hasOwnProperty(key) ? s.requestsStatus[key] : null));
  };

  /** Reset the store but keeps onboarding, language and theme */
  reset() {
    const snapshot = this.store.getValue();
    this.store.reset();
    this.store.update((_s) => ({
      antiGaspiHistory: [],
      allergens: {
        userAllergens: [],
      },
      language: snapshot.language,
      theme: snapshot.theme,
      likes: { categories: [], currentCategory: null, dishFamilies: [], filter: LikesFilterEnum.All },
      requestsStatus: { likes: getIdleRequestStatus(), [AccountStatusKeys.UpdateDishLikeNotification]: getIdleRequestStatus() },
      onboardingInfos: snapshot.onboardingInfos,
      onboardingViewedKeys: snapshot.onboardingViewedKeys,
      onboardingViewedDate: null,
    }));
  }

  public trackRequestStatus = createRequestsStatusOperator(this.store);
}

export const buildFluiditeDsKey = (restaurantId: number) => `fluidite.${restaurantId}`;
export const buildUpdateDishLikeNotificationKey = (dishId: number) => `${AccountStatusKeys.UpdateDishLikeNotification}_${dishId}`;
